import React, { useState, useEffect, useCallback, useRef } from "react";
import MatchCard from "./MatchCard";
import DatePickerCalendar from "./DatePickerCalendar";
import "./Partidos.css";
import "./DatePickerCalendar.css";
import { supabase } from "../supabaseClient";
import {
  startOfWeek,
  addDays,
  endOfMonth,
  isValid,
  format,
  parseISO,
  startOfMonth,
} from "date-fns";
import { formatGameDate, formatGameTime } from "../Functions/Formatting";
import { matchStatus } from "../Functions/matchStatus";
import { NavArrowButton, DayGroup, MatchListSkeleton } from "./MatchListParts";
import StateMessage from "./StateMessage";

const FRESH_MS = 30_000;
const PREFETCH_DELAY_MS = 300;

const CalendarIcon = () => (
  <svg
    className="date-picker-trigger-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const mondayISO = (date) => {
  const monday = startOfWeek(date, { weekStartsOn: 1 });
  return `${format(monday, "yyyy-MM-dd")}T12:00:00.000Z`;
};

const Partidos = ({ leagueFilter, themeColor }) => {
  const [gamesCache, setGamesCache] = useState({});
  const gamesCacheRef = useRef(gamesCache);
  gamesCacheRef.current = gamesCache;

  const [currentWeekISO, setCurrentWeekISO] = useState(() =>
    mondayISO(new Date())
  );
  const [error, setError] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [dateLoading, setDateLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [markedDates, setMarkedDates] = useState(() => new Set());
  const [markedMonthKey, setMarkedMonthKey] = useState("");

  const weekMonday = currentWeekISO.split("T")[0];
  const cacheKey = `${leagueFilter}:${weekMonday}`;

  const fetchSupabaseGames = useCallback(async (leagueId, weekStart) => {
    const weekEnd = format(addDays(parseISO(weekStart), 6), "yyyy-MM-dd");
    const { data: rows, error: fetchError } = await supabase
      .from("games")
      .select(
        "game_id:id, league_id, team1_id, team2_id, team1_score, team2_score, game_date, game_time, status, t1:teams!team1_id(team_name, logo_url, primary_kit_colors), t2:teams!team2_id(team_name, logo_url, primary_kit_colors), leagues!inner(id)"
      )
      .eq("league_id", leagueId)
      .eq("is_published", true)
      .eq("leagues.is_published", true)
      .gte("game_date", weekStart)
      .lte("game_date", weekEnd)
      .order("game_date", { ascending: true })
      .order("game_time", { ascending: true });

    if (fetchError) throw fetchError;

    return (rows || []).map((row) => ({
      ...row,
      team1_name: row.t1?.team_name,
      team2_name: row.t2?.team_name,
      team1_logo_url: row.t1?.logo_url || null,
      team2_logo_url: row.t2?.logo_url || null,
      team1_kit_colors: row.t1?.primary_kit_colors || [],
      team2_kit_colors: row.t2?.primary_kit_colors || [],
    }));
  }, []);

  const fetchMonthMarkers = useCallback(
    async (monthStart) => {
      if (!leagueFilter) return;

      const start = format(startOfMonth(monthStart), "yyyy-MM-dd");
      const end = format(endOfMonth(monthStart), "yyyy-MM-dd");
      const key = `${leagueFilter}:${start.slice(0, 7)}`;
      setMarkedMonthKey(key);

      try {
        const { data, error: markersError } = await supabase
          .from("games")
          .select("game_date, leagues!inner(id)")
          .eq("is_published", true)
          .eq("leagues.is_published", true)
          .eq("league_id", leagueFilter)
          .gte("game_date", start)
          .lte("game_date", end);

        if (markersError) throw markersError;

        const dates = new Set(
          (data || []).map((row) =>
            typeof row.game_date === "string"
              ? row.game_date.slice(0, 10)
              : format(new Date(row.game_date), "yyyy-MM-dd")
          )
        );
        setMarkedDates(dates);
      } catch (err) {
        console.error("Failed to load calendar markers", err);
      }
    },
    [leagueFilter]
  );

  const prefetchWeek = useCallback(
    async (weekISO) => {
      if (!leagueFilter || !weekISO) return;
      const monday = weekISO.split("T")[0];
      const key = `${leagueFilter}:${monday}`;
      const cached = gamesCacheRef.current[key];
      if (cached && Date.now() - cached.ts < FRESH_MS) return;

      try {
        const data = await fetchSupabaseGames(leagueFilter, monday);
        setGamesCache((prev) => ({
          ...prev,
          [key]: { data, ts: Date.now() },
        }));
      } catch {
        // prefetch is best-effort
      }
    },
    [fetchSupabaseGames, leagueFilter]
  );

  useEffect(() => {
    if (!currentWeekISO || !leagueFilter) return;

    const key = `${leagueFilter}:${currentWeekISO.split("T")[0]}`;
    let isMounted = true;
    const cached = gamesCacheRef.current[key];
    const now = Date.now();
    const hasCachedData = Boolean(cached?.data);

    setDateLoading(!hasCachedData);

    (async () => {
      try {
        if (cached && now - cached.ts < FRESH_MS) {
          if (isMounted) {
            setLoadingInitial(false);
            setDateLoading(false);
          }
          return;
        }

        const data = await fetchSupabaseGames(
          leagueFilter,
          currentWeekISO.split("T")[0]
        );
        setGamesCache((prev) => ({
          ...prev,
          [key]: { data, ts: Date.now() },
        }));
        if (isMounted) {
          setLoadingInitial(false);
          setDateLoading(false);
        }
      } catch (err) {
        console.error("League week fetch failed", err);
        if (isMounted) {
          if (!hasCachedData) setError("Failed to load games data.");
          setLoadingInitial(false);
          setDateLoading(false);
        }
      } finally {
        if (isMounted) {
          prefetchWeek(addDays(new Date(currentWeekISO), 7).toISOString());
          prefetchWeek(addDays(new Date(currentWeekISO), -7).toISOString());
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [currentWeekISO, leagueFilter, fetchSupabaseGames, prefetchWeek]);

  useEffect(() => {
    if (!currentWeekISO || !leagueFilter) return;
    const id = setTimeout(() => {
      const base = new Date(currentWeekISO);
      prefetchWeek(addDays(base, 14).toISOString());
      prefetchWeek(addDays(base, -14).toISOString());
    }, PREFETCH_DELAY_MS);
    return () => clearTimeout(id);
  }, [currentWeekISO, leagueFilter, prefetchWeek]);

  useEffect(() => {
    setMarkedDates(new Set());
    setMarkedMonthKey("");
  }, [leagueFilter]);

  const changePeriod = (days) => {
    setError(null);
    const newDate = addDays(new Date(currentWeekISO), days);
    if (isValid(newDate)) {
      setCurrentWeekISO(mondayISO(newDate));
    }
  };

  const handleMonthChange = useCallback(
    (monthStart) => {
      const key = `${leagueFilter}:${format(monthStart, "yyyy-MM")}`;
      if (key === markedMonthKey) return;
      fetchMonthMarkers(monthStart);
    },
    [fetchMonthMarkers, leagueFilter, markedMonthKey]
  );

  const gamesForCurrentWeek = gamesCache[cacheKey]?.data || [];
  const showListLoading =
    (loadingInitial || dateLoading) && gamesForCurrentWeek.length === 0;

  const groupedGames = gamesForCurrentWeek.reduce((acc, game) => {
    const dateStr = game.game_date;
    if (!acc[dateStr]) acc[dateStr] = [];
    acc[dateStr].push(game);
    return acc;
  }, {});
  const dates = Object.keys(groupedGames).sort();

  return (
    <section className="matchcard-container" aria-labelledby="schedule-title">
      <header className="list-block-head">
        <h2 className="block-title" id="schedule-title">
          Calendario
        </h2>
      </header>
      <div className="period-navigation">
        <NavArrowButton
          direction="prev"
          label="Semana anterior"
          onClick={() => changePeriod(-7)}
        />

        <div className={`date-picker-shell${calendarOpen ? " open" : ""}`}>
          <button
            type="button"
            className="date-picker-trigger"
            onClick={() => setCalendarOpen((o) => !o)}
            aria-expanded={calendarOpen}
            aria-haspopup="dialog"
          >
            <span className="semana-text">
              <span className="semana-prefix">Semana del</span>{" "}
              {formatGameDate(currentWeekISO)}
            </span>
            <CalendarIcon />
          </button>

          {calendarOpen && (
            <>
              <button
                type="button"
                className="date-picker-backdrop"
                aria-label="Cerrar calendario"
                onClick={() => setCalendarOpen(false)}
              />
              <DatePickerCalendar
                selectedISO={currentWeekISO}
                markedDates={markedDates}
                onSelect={(iso) => {
                  setError(null);
                  setCurrentWeekISO(mondayISO(parseISO(iso.split("T")[0])));
                }}
                onMonthChange={handleMonthChange}
                onClose={() => setCalendarOpen(false)}
              />
            </>
          )}
        </div>

        <NavArrowButton
          direction="next"
          label="Semana siguiente"
          onClick={() => changePeriod(7)}
        />
      </div>

      {dateLoading && gamesForCurrentWeek.length > 0 && (
        <div className="subtle-badge" role="status">Actualizando…</div>
      )}

      {error ? (
        <StateMessage kind="error" title="No se pudieron cargar los partidos.">
          Revisa tu conexión e intenta de nuevo.
        </StateMessage>
      ) : showListLoading ? (
        <MatchListSkeleton />
      ) : gamesForCurrentWeek.length > 0 ? (
        dates.map((dateStr) => {
          return (
            <DayGroup key={dateStr} dateStr={dateStr}>
              {groupedGames[dateStr].map((game) => (
                <MatchCard
                  key={game.game_id}
                  team1={game.team1_name}
                  team1Logo={game.team1_logo_url}
                  team1KitColors={game.team1_kit_colors}
                  team2={game.team2_name}
                  team2Logo={game.team2_logo_url}
                  team2KitColors={game.team2_kit_colors}
                  time={formatGameTime(game.game_time)}
                  date={formatGameDate(game.game_date)}
                  status={matchStatus(game)}
                  team1Score={game.team1_score}
                  team2Score={game.team2_score}
                  league={game.league_id}
                  displayLeague={false}
                  gameID={game.game_id}
                  themeColor={themeColor}
                />
              ))}
            </DayGroup>
          );
        })
      ) : (
        <StateMessage title="Sin partidos esta semana">
          Usa las flechas o el calendario para ver otras semanas.
        </StateMessage>
      )}
    </section>
  );
};

export default Partidos;
